import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  getProject, 
  getBudgetItems, 
  getCategories,
  getSubcategories,
  createBudgetItem,
  updateBudgetItem,
  deleteBudgetItem,
  exportProjectPDF,
  getInstallments,
  createInstallment,
  updateInstallment,
  markInstallmentPaid,
  uploadPaymentProof,
  deleteInstallment,
  getPaymentProofUrl
} from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { 
  ArrowLeft, 
  Plus, 
  FileDown, 
  Calendar, 
  MapPin,
  Edit,
  Trash2,
  CreditCard,
  Upload,
  Check,
  X,
  Eye,
  Loader2,
  DollarSign,
  Clock,
  LayoutGrid,
  Table as TableIcon,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

export const ProjectDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isLeader, user } = useAuth();
  
  const [project, setProject] = useState(null);
  const [budgetItems, setBudgetItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  
  // View mode: 'card' or 'table'
  const [viewMode, setViewMode] = useState('card');
  
  // Category filter for table view
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  // Collapsible state for each category
  const [expandedCategories, setExpandedCategories] = useState({});
  
  // All installments by budget item id
  const [allInstallments, setAllInstallments] = useState({});
  
  // Budget Item Modal
  const [showItemModal, setShowItemModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemForm, setItemForm] = useState({
    category_id: '',
    subcategory_id: '',
    vendor_name: '',
    estimated_budget: '',
    realization: '',
    status: 'Screening',
    timeline_start: '',
    timeline_end: '',
    notes: ''
  });
  
  // Installment Modal
  const [showInstallmentModal, setShowInstallmentModal] = useState(false);
  const [selectedBudgetItem, setSelectedBudgetItem] = useState(null);
  const [installments, setInstallments] = useState([]);
  const [installmentForm, setInstallmentForm] = useState({
    amount: '',
    due_date: '',
    description: ''
  });
  const [editingInstallment, setEditingInstallment] = useState(null);
  
  // Delete confirmations
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [deleteInstallmentId, setDeleteInstallmentId] = useState(null);
  
  // Image viewer
  const [viewingImage, setViewingImage] = useState(null);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [projectRes, itemsRes, categoriesRes] = await Promise.all([
        getProject(id),
        getBudgetItems(id),
        getCategories()
      ]);
      setProject(projectRes.data);
      setBudgetItems(itemsRes.data);
      setCategories(categoriesRes.data);
      
      // Initialize expanded state for all categories
      const expanded = {};
      categoriesRes.data.forEach(cat => {
        expanded[cat.id] = true;
      });
      setExpandedCategories(expanded);
      
      // Fetch all installments for table view
      const installmentPromises = itemsRes.data.map(item => 
        getInstallments(item.id).then(res => ({ itemId: item.id, data: res.data }))
      );
      const installmentResults = await Promise.all(installmentPromises);
      const installmentMap = {};
      installmentResults.forEach(result => {
        installmentMap[result.itemId] = result.data;
      });
      setAllInstallments(installmentMap);
    } catch (error) {
      toast.error('Failed to fetch project details');
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubcategories = async (categoryId) => {
    try {
      const response = await getSubcategories(categoryId);
      setSubcategories(response.data);
    } catch (error) {
      console.error('Failed to fetch subcategories');
    }
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const response = await exportProjectPDF(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `knots_budget_${project.bride_name}_${project.groom_name}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF exported successfully');
    } catch (error) {
      toast.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  // Budget Item handlers
  const openItemModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setItemForm({
        category_id: item.category_id,
        subcategory_id: item.subcategory_id || '',
        vendor_name: item.vendor_name || '',
        estimated_budget: item.estimated_budget.toString(),
        realization: item.realization.toString(),
        status: item.status,
        timeline_start: item.timeline_start || '',
        timeline_end: item.timeline_end || '',
        notes: item.notes || ''
      });
      if (item.category_id) {
        fetchSubcategories(item.category_id);
      }
    } else {
      setEditingItem(null);
      setItemForm({
        category_id: '',
        subcategory_id: '',
        vendor_name: '',
        estimated_budget: '',
        realization: '',
        status: 'Screening',
        timeline_start: '',
        timeline_end: '',
        notes: ''
      });
      setSubcategories([]);
    }
    setShowItemModal(true);
  };

  const handleSaveItem = async () => {
    if (!itemForm.category_id) {
      toast.error('Please select a category');
      return;
    }
    
    try {
      const data = {
        ...itemForm,
        project_id: id,
        item_name: '',
        estimated_budget: parseFloat(itemForm.estimated_budget) || 0,
        realization: parseFloat(itemForm.realization) || 0
      };
      
      if (editingItem) {
        await updateBudgetItem(editingItem.id, data);
        toast.success('Item updated');
      } else {
        await createBudgetItem(data);
        toast.success('Item created');
      }
      
      setShowItemModal(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save item');
    }
  };

  const handleDeleteItem = async () => {
    if (!deleteItemId) return;
    try {
      await deleteBudgetItem(deleteItemId);
      toast.success('Item deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete item');
    } finally {
      setDeleteItemId(null);
    }
  };

  // Installment handlers
  const openInstallmentModal = async (budgetItem) => {
    setSelectedBudgetItem(budgetItem);
    setEditingInstallment(null);
    setInstallmentForm({ amount: '', due_date: '', description: '' });
    try {
      const response = await getInstallments(budgetItem.id);
      setInstallments(response.data);
    } catch (error) {
      toast.error('Failed to fetch installments');
      setInstallments([]);
    }
    setShowInstallmentModal(true);
  };

  const handleSaveInstallment = async () => {
    if (!installmentForm.amount || !installmentForm.due_date) {
      toast.error('Please fill required fields');
      return;
    }
    
    try {
      const data = {
        ...installmentForm,
        budget_item_id: selectedBudgetItem.id,
        amount: parseFloat(installmentForm.amount)
      };
      
      if (editingInstallment) {
        await updateInstallment(editingInstallment.id, data);
        toast.success('Installment updated');
      } else {
        await createInstallment(data);
        toast.success('Installment created');
      }
      
      const response = await getInstallments(selectedBudgetItem.id);
      setInstallments(response.data);
      setEditingInstallment(null);
      setInstallmentForm({ amount: '', due_date: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to save installment');
    }
  };

  const handleUploadProof = async (installmentId, file) => {
    try {
      await uploadPaymentProof(installmentId, file);
      toast.success('Payment proof uploaded');
      const response = await getInstallments(selectedBudgetItem.id);
      setInstallments(response.data);
      fetchData();
    } catch (error) {
      toast.error('Failed to upload proof');
    }
  };

  const handleMarkPaid = async (installmentId) => {
    try {
      await markInstallmentPaid(installmentId);
      toast.success('Marked as paid');
      const response = await getInstallments(selectedBudgetItem.id);
      setInstallments(response.data);
      fetchData();
    } catch (error) {
      toast.error('Failed to mark as paid');
    }
  };

  const handleDeleteInstallment = async () => {
    if (!deleteInstallmentId) return;
    try {
      await deleteInstallment(deleteInstallmentId);
      toast.success('Installment deleted');
      const response = await getInstallments(selectedBudgetItem.id);
      setInstallments(response.data);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete installment');
    } finally {
      setDeleteInstallmentId(null);
    }
  };

  // Permission helpers
  const canClientEdit = () => {
    if (isLeader()) return true;
    if (!project) return false;
    // Wedding Planner: client is view-only
    if (project.event_category === 'Wedding Planner') return false;
    // Wedding Organizer / Sangjit: client can add/edit
    return true;
  };

  const canDelete = () => {
    // Only leader can delete
    return isLeader();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatCurrencyShort = (amount) => {
    if (amount >= 1000000) {
      return `${(amount / 1000000).toFixed(1)}M`;
    }
    if (amount >= 1000) {
      return `${(amount / 1000).toFixed(0)}K`;
    }
    return amount.toString();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      'Screening': 'bg-gray-100 text-gray-700 border-gray-300',
      'Deal': 'bg-blue-50 text-blue-700 border-blue-300',
      'Progress': 'bg-amber-50 text-amber-700 border-amber-300',
      'Done': 'bg-green-50 text-green-700 border-green-300'
    };
    return styles[status] || styles['Screening'];
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  // Group items by category for table view
  const itemsByCategory = useMemo(() => {
    const grouped = {};
    categories.forEach(cat => {
      grouped[cat.id] = {
        category: cat,
        items: budgetItems.filter(item => item.category_id === cat.id)
      };
    });
    return grouped;
  }, [categories, budgetItems]);

  // Filter categories for table view
  const filteredCategories = useMemo(() => {
    if (categoryFilter === 'all') return categories;
    return categories.filter(cat => cat.id === categoryFilter);
  }, [categories, categoryFilter]);

  // Calculate progress for an item
  const calculateProgress = (item) => {
    if (!item.realization || item.realization === 0) return 0;
    const itemInstallments = allInstallments[item.id] || [];
    const totalPaid = itemInstallments
      .filter(i => i.status === 'Paid')
      .reduce((sum, i) => sum + i.amount, 0);
    return Math.round((totalPaid / item.realization) * 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-imperial" />
      </div>
    );
  }

  if (!project) return null;

  const totalEstimated = budgetItems.reduce((sum, item) => sum + (item.estimated_budget || 0), 0);
  const totalRealized = budgetItems.reduce((sum, item) => sum + (item.realization || 0), 0);
  const totalPaid = budgetItems.reduce((sum, item) => sum + (item.total_paid || 0), 0);

  return (
    <div data-testid="project-detail-page" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/projects')}
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-3xl font-bold text-imperial">
              {project.bride_name} & {project.groom_name}
            </h1>
            <div className="flex flex-wrap items-center gap-4 mt-2 text-muted-foreground">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {formatDate(project.wedding_date)}
              </span>
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {project.location}
              </span>
            </div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            onClick={handleExportPDF}
            disabled={exporting}
            className="bg-gold hover:bg-gold/90 text-white"
            data-testid="export-pdf-button"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileDown className="w-4 h-4 mr-2" />
            )}
            Export PDF
          </Button>
          {canClientEdit() && (
            <Button
              onClick={() => openItemModal()}
              className="bg-imperial hover:bg-imperial/90"
              data-testid="add-budget-item-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Item
            </Button>
          )}
        </div>
      </div>

      {/* Event Category Info for Client */}
      {!isLeader() && project.event_category === 'Wedding Planner' && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="text-amber-800 text-sm">
            <strong>View Only Mode:</strong> This Wedding Planner project is managed by your planner. Contact them for any changes.
          </p>
        </div>
      )}

      {/* Budget Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="card-luxury">
          <CardContent className="p-4">
            <p className="font-body text-xs uppercase tracking-widest text-muted-foreground">Target Budget</p>
            <p className="font-heading text-lg md:text-xl font-bold mt-1">{formatCurrency(project.target_budget)}</p>
          </CardContent>
        </Card>
        <Card className="card-luxury">
          <CardContent className="p-4">
            <p className="font-body text-xs uppercase tracking-widest text-muted-foreground">Estimated</p>
            <p className="font-heading text-lg md:text-xl font-bold mt-1">{formatCurrency(totalEstimated)}</p>
          </CardContent>
        </Card>
        <Card className="card-luxury">
          <CardContent className="p-4">
            <p className="font-body text-xs uppercase tracking-widest text-muted-foreground">Realization</p>
            <p className="font-heading text-lg md:text-xl font-bold mt-1 text-imperial">{formatCurrency(totalRealized)}</p>
          </CardContent>
        </Card>
        <Card className="card-luxury">
          <CardContent className="p-4">
            <p className="font-body text-xs uppercase tracking-widest text-muted-foreground">Total Paid</p>
            <p className="font-heading text-lg md:text-xl font-bold mt-1 text-green-600">{formatCurrency(totalPaid)}</p>
          </CardContent>
        </Card>
      </div>

      {/* View Toggle & Filters */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <h2 className="font-heading text-2xl font-semibold">Budget Items</h2>
        <div className="flex items-center gap-2">
          {/* View Mode Toggle */}
          <div className="flex bg-white border border-warm-grey rounded-md p-1">
            <Button
              variant={viewMode === 'card' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('card')}
              className={viewMode === 'card' ? 'bg-imperial text-white' : ''}
              data-testid="view-card-button"
            >
              <LayoutGrid className="w-4 h-4 mr-1" />
              Card
            </Button>
            <Button
              variant={viewMode === 'table' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('table')}
              className={viewMode === 'table' ? 'bg-imperial text-white' : ''}
              data-testid="view-table-button"
            >
              <TableIcon className="w-4 h-4 mr-1" />
              Table
            </Button>
          </div>
        </div>
      </div>

      {/* Category Filter for Table View */}
      {viewMode === 'table' && (
        <div className="flex flex-wrap gap-2">
          <Button
            variant={categoryFilter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setCategoryFilter('all')}
            className={categoryFilter === 'all' ? 'bg-[#1e3a5f] text-white' : ''}
            data-testid="filter-all"
          >
            All
          </Button>
          {categories.map(cat => (
            <Button
              key={cat.id}
              variant={categoryFilter === cat.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setCategoryFilter(cat.id)}
              className={categoryFilter === cat.id ? 'bg-[#1e3a5f] text-white' : ''}
              data-testid={`filter-${cat.name.toLowerCase()}`}
            >
              {cat.name}
            </Button>
          ))}
        </div>
      )}

      {/* Budget Items Content */}
      {budgetItems.length === 0 ? (
        <Card className="card-luxury">
          <CardContent className="p-12 text-center">
            <DollarSign className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
            <p className="font-body text-muted-foreground">No budget items yet</p>
            {canClientEdit() && (
              <Button 
                onClick={() => openItemModal()}
                className="mt-4 bg-imperial hover:bg-imperial/90"
              >
                Add First Item
              </Button>
            )}
          </CardContent>
        </Card>
      ) : viewMode === 'card' ? (
        /* Card View */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {budgetItems.map((item) => (
            <Card key={item.id} className="card-luxury" data-testid={`budget-item-${item.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="font-heading text-lg">
                      {item.subcategory_name || item.category_name}
                    </CardTitle>
                    <p className="font-body text-sm text-muted-foreground">{item.vendor_name || '-'}</p>
                  </div>
                  <Badge className={`${getStatusBadge(item.status)} border`}>
                    {item.status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-xs text-muted-foreground font-body uppercase tracking-wide">
                  {item.category_name}
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="font-body text-sm text-muted-foreground">Estimated</span>
                    <span className="font-body text-sm font-medium">{formatCurrency(item.estimated_budget)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-body text-sm text-muted-foreground">Realization</span>
                    <span className="font-body text-sm font-medium text-imperial">{formatCurrency(item.realization)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-body text-sm text-muted-foreground">Paid</span>
                    <span className="font-body text-sm font-medium text-green-600">{formatCurrency(item.total_paid)}</span>
                  </div>
                </div>

                {(item.timeline_start || item.timeline_end) && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="w-4 h-4" />
                    <span>{formatDate(item.timeline_start)} - {formatDate(item.timeline_end)}</span>
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  <Button 
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => openInstallmentModal(item)}
                    data-testid={`installments-${item.id}`}
                  >
                    <CreditCard className="w-4 h-4 mr-1" />
                    Installments
                  </Button>
                  {canClientEdit() && (
                    <Button 
                      variant="outline"
                      size="icon"
                      onClick={() => openItemModal(item)}
                      data-testid={`edit-item-${item.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                  )}
                  {canDelete() && (
                    <Button 
                      variant="outline"
                      size="icon"
                      className="text-destructive hover:bg-destructive hover:text-white"
                      onClick={() => setDeleteItemId(item.id)}
                      data-testid={`delete-item-${item.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        /* Table View - Budget Breakdown */
        <Card className="card-luxury overflow-hidden">
          <div className="overflow-x-auto">
            {/* Table Header */}
            <div className="min-w-[1300px]">
              <div className="grid grid-cols-13 bg-[#1e3a5f] text-white text-xs font-bold uppercase tracking-wider">
                <div className="col-span-2 p-3 border-r border-[#2d4a6f]">Category / Detail</div>
                <div className="col-span-1 p-3 border-r border-[#2d4a6f] text-center">Start Date</div>
                <div className="col-span-1 p-3 border-r border-[#2d4a6f] text-center bg-[#6b5b95]">Status</div>
                <div className="col-span-1 p-3 border-r border-[#2d4a6f] text-center bg-[#45b7aa]">Progress</div>
                <div className="col-span-1 p-3 border-r border-[#2d4a6f]">Vendor</div>
                <div className="col-span-1 p-3 border-r border-[#2d4a6f] text-center bg-[#c9a227]">Est. Cost</div>
                <div className="col-span-1 p-3 border-r border-[#2d4a6f] text-center bg-[#c27ba0]">Agreed Cost</div>
                <div className="col-span-1 p-3 border-r border-[#2d4a6f] text-center bg-[#e67e22]">Cost %</div>
                <div className="col-span-1 p-3 border-r border-[#2d4a6f] text-center">DP</div>
                <div className="col-span-1 p-3 border-r border-[#2d4a6f] text-center">Term 2</div>
                <div className="col-span-1 p-3 text-center">Actions</div>
              </div>

              {/* Table Body - Grouped by Category */}
              {filteredCategories.map(category => {
                const categoryItems = itemsByCategory[category.id]?.items || [];
                if (categoryItems.length === 0 && categoryFilter === 'all') return null;
                
                return (
                  <Collapsible
                    key={category.id}
                    open={expandedCategories[category.id]}
                    onOpenChange={() => toggleCategory(category.id)}
                  >
                    {/* Category Header */}
                    <CollapsibleTrigger asChild>
                      <div 
                        className="grid grid-cols-13 bg-[#1e3a5f] text-white cursor-pointer hover:bg-[#2d4a6f] transition-colors"
                        data-testid={`category-header-${category.id}`}
                      >
                        <div className="col-span-13 p-3 flex items-center gap-2">
                          {expandedCategories[category.id] ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                          <span className="font-bold uppercase">{category.name}</span>
                          <span className="text-white/70 text-sm font-normal">{categoryItems.length} items</span>
                        </div>
                      </div>
                    </CollapsibleTrigger>

                    {/* Category Items */}
                    <CollapsibleContent>
                      {categoryItems.length === 0 ? (
                        <div className="grid grid-cols-13 bg-cream/50">
                          <div className="col-span-13 p-4 text-center text-muted-foreground text-sm">
                            No items in this category
                          </div>
                        </div>
                      ) : (
                        categoryItems.map((item, idx) => {
                          const itemInstallments = allInstallments[item.id] || [];
                          const progress = calculateProgress(item);
                          const costPercentage = totalRealized > 0 
                            ? ((item.realization / totalRealized) * 100).toFixed(1) 
                            : 0;
                          
                          // Get installments sorted by date for DP, Term 2
                          const sortedInstallments = [...itemInstallments].sort((a, b) => 
                            new Date(a.due_date) - new Date(b.due_date)
                          );
                          
                          return (
                            <div 
                              key={item.id}
                              className={`grid grid-cols-13 border-b border-warm-grey text-sm ${idx % 2 === 0 ? 'bg-white' : 'bg-cream/30'}`}
                              data-testid={`table-item-${item.id}`}
                            >
                              {/* Detail */}
                              <div className="col-span-2 p-3 border-r border-warm-grey">
                                <p className="font-medium text-foreground">
                                  {item.subcategory_name || item.category_name}
                                </p>
                              </div>
                              
                              {/* Start Date */}
                              <div className="col-span-1 p-3 border-r border-warm-grey text-center flex items-center justify-center">
                                <span className="text-muted-foreground">{formatDate(item.timeline_start)}</span>
                              </div>
                              
                              {/* Status */}
                              <div className="col-span-1 p-3 border-r border-warm-grey text-center flex items-center justify-center">
                                <Badge className={`${getStatusBadge(item.status)} border text-xs`}>
                                  {item.status}
                                </Badge>
                              </div>
                              
                              {/* Progress */}
                              <div className="col-span-1 p-3 border-r border-warm-grey text-center flex items-center justify-center">
                                <div className="flex items-center gap-1">
                                  <div className="w-12 h-2 bg-gray-200 rounded-full overflow-hidden">
                                    <div 
                                      className="h-full bg-[#45b7aa] rounded-full transition-all"
                                      style={{ width: `${Math.min(progress, 100)}%` }}
                                    />
                                  </div>
                                  <span className="text-xs text-muted-foreground">{progress}%</span>
                                </div>
                              </div>
                              
                              {/* Vendor */}
                              <div className="col-span-1 p-3 border-r border-warm-grey flex items-center">
                                <span className="text-muted-foreground truncate">{item.vendor_name || '-'}</span>
                              </div>
                              
                              {/* Est. Cost */}
                              <div className="col-span-1 p-3 border-r border-warm-grey text-center flex items-center justify-center">
                                <span className="font-medium">{formatCurrencyShort(item.estimated_budget)}</span>
                              </div>
                              
                              {/* Agreed Cost */}
                              <div className="col-span-1 p-3 border-r border-warm-grey text-center flex items-center justify-center">
                                <span className="font-medium text-imperial">{formatCurrencyShort(item.realization)}</span>
                              </div>
                              
                              {/* Cost % */}
                              <div className="col-span-1 p-3 border-r border-warm-grey text-center flex items-center justify-center">
                                <span className="font-medium text-orange-600">{costPercentage}%</span>
                              </div>
                              
                              {/* DP (First Installment) */}
                              <div className="col-span-1 p-3 border-r border-warm-grey text-center flex items-center justify-center">
                                {sortedInstallments[0] ? (
                                  <span className={sortedInstallments[0].status === 'Paid' ? 'text-green-600' : 'text-muted-foreground'}>
                                    {formatCurrencyShort(sortedInstallments[0].amount)}
                                  </span>
                                ) : <span className="text-muted-foreground">-</span>}
                              </div>
                              
                              {/* Term 2 */}
                              <div className="col-span-1 p-3 border-r border-warm-grey text-center flex items-center justify-center">
                                {sortedInstallments[1] ? (
                                  <span className={sortedInstallments[1].status === 'Paid' ? 'text-green-600' : 'text-muted-foreground'}>
                                    {formatCurrencyShort(sortedInstallments[1].amount)}
                                  </span>
                                ) : <span className="text-muted-foreground">-</span>}
                              </div>
                              
                              {/* Actions */}
                              <div className="col-span-1 p-3 flex items-center justify-center gap-1">
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => openInstallmentModal(item)}
                                  data-testid={`table-installments-${item.id}`}
                                >
                                  <CreditCard className="w-4 h-4" />
                                </Button>
                                {canClientEdit() && (
                                  <Button 
                                    variant="ghost" 
                                    size="icon"
                                    className="h-7 w-7"
                                    onClick={() => openItemModal(item)}
                                    data-testid={`table-edit-${item.id}`}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                )}
                                {canDelete() && (
                                  <Button 
                                    variant="ghost" 
                                    size="icon"
                                    className="h-7 w-7 text-destructive hover:bg-destructive/10"
                                    onClick={() => setDeleteItemId(item.id)}
                                    data-testid={`table-delete-${item.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </CollapsibleContent>
                  </Collapsible>
                );
              })}
            </div>
          </div>
        </Card>
      )}

      {/* Budget Item Modal */}
      <Dialog open={showItemModal} onOpenChange={setShowItemModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editingItem ? 'Edit Budget Item' : 'Add Budget Item'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Category *</Label>
                <Select 
                  value={itemForm.category_id} 
                  onValueChange={(value) => {
                    setItemForm({ ...itemForm, category_id: value, subcategory_id: '' });
                    fetchSubcategories(value);
                  }}
                >
                  <SelectTrigger data-testid="category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Subcategory</Label>
                <Select 
                  value={itemForm.subcategory_id} 
                  onValueChange={(value) => setItemForm({ ...itemForm, subcategory_id: value })}
                >
                  <SelectTrigger data-testid="subcategory-select">
                    <SelectValue placeholder="Select subcategory" />
                  </SelectTrigger>
                  <SelectContent>
                    {subcategories.map(sub => (
                      <SelectItem key={sub.id} value={sub.id}>{sub.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Vendor Name</Label>
              <Input
                value={itemForm.vendor_name}
                onChange={(e) => setItemForm({ ...itemForm, vendor_name: e.target.value })}
                placeholder="e.g., Vera Wang"
                data-testid="vendor-name-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Estimated Budget *</Label>
                <Input
                  type="number"
                  value={itemForm.estimated_budget}
                  onChange={(e) => setItemForm({ ...itemForm, estimated_budget: e.target.value })}
                  placeholder="0"
                  data-testid="estimated-budget-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Realization (Agreed)</Label>
                <Input
                  type="number"
                  value={itemForm.realization}
                  onChange={(e) => setItemForm({ ...itemForm, realization: e.target.value })}
                  placeholder="0"
                  data-testid="realization-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Status</Label>
              <Select 
                value={itemForm.status} 
                onValueChange={(value) => setItemForm({ ...itemForm, status: value })}
              >
                <SelectTrigger data-testid="status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Screening">Screening</SelectItem>
                  <SelectItem value="Deal">Deal</SelectItem>
                  <SelectItem value="Progress">Progress</SelectItem>
                  <SelectItem value="Done">Done</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Timeline Start</Label>
                <Input
                  type="date"
                  value={itemForm.timeline_start}
                  onChange={(e) => setItemForm({ ...itemForm, timeline_start: e.target.value })}
                  data-testid="timeline-start-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Timeline End</Label>
                <Input
                  type="date"
                  value={itemForm.timeline_end}
                  onChange={(e) => setItemForm({ ...itemForm, timeline_end: e.target.value })}
                  data-testid="timeline-end-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Notes</Label>
              <Textarea
                value={itemForm.notes}
                onChange={(e) => setItemForm({ ...itemForm, notes: e.target.value })}
                placeholder="Additional notes..."
                data-testid="item-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowItemModal(false)}>Cancel</Button>
            <Button onClick={handleSaveItem} className="bg-imperial hover:bg-imperial/90" data-testid="save-item-button">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Installment Modal */}
      <Dialog open={showInstallmentModal} onOpenChange={setShowInstallmentModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              Installments - {selectedBudgetItem?.subcategory_name || selectedBudgetItem?.category_name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Summary */}
            {selectedBudgetItem && (
              <div className="flex gap-4 p-4 bg-cream rounded-lg">
                <div>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground">Contract Price</p>
                  <p className="font-bold">{formatCurrency(selectedBudgetItem.realization || 0)}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground">Total Paid</p>
                  <p className="font-bold text-green-600">{formatCurrency(selectedBudgetItem.total_paid || 0)}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground">Remaining</p>
                  <p className="font-bold text-imperial">
                    {formatCurrency((selectedBudgetItem.realization || 0) - (selectedBudgetItem.total_paid || 0))}
                  </p>
                </div>
              </div>
            )}

            {/* Installments List */}
            {installments.length > 0 && (
              <div className="space-y-3">
                {installments.map((inst) => (
                  <div key={inst.id} className="flex items-center gap-4 p-4 border border-warm-grey rounded-lg" data-testid={`installment-${inst.id}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{formatCurrency(inst.amount)}</span>
                        <Badge className={inst.status === 'Paid' ? 'badge-paid border' : 'badge-unpaid border'}>
                          {inst.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        Due: {formatDate(inst.due_date)}
                        {inst.description && ` - ${inst.description}`}
                      </p>
                      {inst.paid_at && (
                        <p className="text-xs text-green-600 mt-1">Paid on {formatDate(inst.paid_at)}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {inst.payment_proof && (
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => setViewingImage(getPaymentProofUrl(inst.payment_proof))}
                          data-testid={`view-proof-${inst.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      )}
                      {canClientEdit() && inst.status === 'Unpaid' && (
                        <>
                          <label className="cursor-pointer">
                            <input
                              type="file"
                              className="hidden"
                              accept="image/*"
                              onChange={(e) => {
                                if (e.target.files[0]) {
                                  handleUploadProof(inst.id, e.target.files[0]);
                                }
                              }}
                            />
                            <Button variant="outline" size="icon" asChild>
                              <span><Upload className="w-4 h-4" /></span>
                            </Button>
                          </label>
                          <Button
                            variant="outline"
                            size="icon"
                            className="text-green-600 hover:bg-green-50"
                            onClick={() => handleMarkPaid(inst.id)}
                            data-testid={`mark-paid-${inst.id}`}
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      {canDelete() && (
                        <Button
                          variant="outline"
                          size="icon"
                          className="text-destructive hover:bg-destructive hover:text-white"
                          onClick={() => setDeleteInstallmentId(inst.id)}
                          data-testid={`delete-installment-${inst.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add Installment Form */}
            {canClientEdit() && (
              <div className="border-t border-warm-grey pt-4">
                <h4 className="font-medium mb-3">{editingInstallment ? 'Edit Installment' : 'Add Installment'}</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-widest text-muted-foreground">Amount *</Label>
                    <Input
                      type="number"
                      value={installmentForm.amount}
                      onChange={(e) => setInstallmentForm({ ...installmentForm, amount: e.target.value })}
                      placeholder="0"
                      data-testid="installment-amount-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-widest text-muted-foreground">Due Date *</Label>
                    <Input
                      type="date"
                      value={installmentForm.due_date}
                      onChange={(e) => setInstallmentForm({ ...installmentForm, due_date: e.target.value })}
                      data-testid="installment-due-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-widest text-muted-foreground">Description</Label>
                    <Input
                      value={installmentForm.description}
                      onChange={(e) => setInstallmentForm({ ...installmentForm, description: e.target.value })}
                      placeholder="e.g., DP 50%"
                      data-testid="installment-description-input"
                    />
                  </div>
                </div>
                <Button 
                  onClick={handleSaveInstallment} 
                  className="mt-4 bg-imperial hover:bg-imperial/90"
                  data-testid="save-installment-button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {editingInstallment ? 'Update' : 'Add'} Installment
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Viewer */}
      <Dialog open={!!viewingImage} onOpenChange={() => setViewingImage(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Payment Proof</DialogTitle>
          </DialogHeader>
          {viewingImage && (
            <img src={viewingImage} alt="Payment Proof" className="w-full rounded-lg" />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmations */}
      <AlertDialog open={!!deleteItemId} onOpenChange={() => setDeleteItemId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Budget Item?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete the item and all its installments. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteItem} className="bg-destructive hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteInstallmentId} onOpenChange={() => setDeleteInstallmentId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Installment?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this installment record.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteInstallment} className="bg-destructive hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProjectDetailPage;
